import {PeopleType} from "src/app/enums/People";
import {WorkEntityKind} from "src/app/enums/WorkEntityKind.enum";
import {WorkEntitySuggestion} from "src/app/modules/shared/interfaces/WorkEntitySuggestion.interface";

/** "Stephen King" and "Stephen king" are the same name to a reader. */
function normalizePeopleName(name: string): string {
  return name.trim().toLocaleLowerCase();
}

/**
 * Names shared by several people credited in different roles. Only those need their
 * role spelled out on the chip: everywhere else the name alone stays unambiguous.
 */
export function findAmbiguousPeopleNames(
  entities: WorkEntitySuggestion[]
): Set<string> {
  const rolesByName = new Map<string, Set<string>>();

  for (const entity of entities) {
    if (entity.kind !== WorkEntityKind.People || !entity.peopleType) {
      continue;
    }

    const key = normalizePeopleName(entity.name);
    const roles = rolesByName.get(key) ?? new Set<string>();
    roles.add(entity.peopleType);
    rolesByName.set(key, roles);
  }

  return new Set(
    [...rolesByName].filter(([, roles]) => roles.size > 1).map(([name]) => name)
  );
}

/** True when the chip must spell its role out to stay readable. */
export function isAmbiguousPeopleName(
  entity: WorkEntitySuggestion,
  ambiguousNames: ReadonlySet<string>
): boolean {
  return ambiguousNames.has(normalizePeopleName(entity.name));
}

/**
 * Role carried by a typed people tag (`tmdb:525|director`). Legacy untyped tags
 * return nothing: they keep selecting every role.
 */
export function parsePeopleTypeFromTagId(
  tagId: string
): PeopleType | undefined {
  const separatorIndex = tagId.lastIndexOf("|");
  if (separatorIndex < 0) {
    return undefined;
  }

  const type = tagId.slice(separatorIndex + 1) as PeopleType;
  return Object.values(PeopleType).includes(type) ? type : undefined;
}
